/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { ChangeEvent, FC, SyntheticEvent, useEffect, useMemo, useState } from "react";
import Box from "components/generals/Box";
import Autocomplete from "components/generals/Autocomplete";
import BillingEncounterTable from "components/tables/BillingEncounterTable";
import { useTranslation } from "react-i18next";
import { AutoCompleteOption, EntityPermissions, PermissionsUser } from "types";
import {
  AppointmentInterface,
  HeaderEnum,
  PatientInterface,
  QueryResultInterface,
  BillingEncounterInterface
} from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Button  from "components/generals/Button";
import Grid from "@mui/material/Grid";
import { formatDateForAPI, getFromISOYYYYMMDDDateFormat } from "utils/date.utils";
import PermissionWrapper from "components/generals/PermissionWrapper";
import TextField from 'components/generals/TextField';

/**
 * Content component for managing billing encounters.
 * Provides functionality for creating and viewing billing encounters associated with
 * patient appointments. Includes patient and appointment selection, date filtering,
 * and billing encounter table display with search and pagination.
 * @returns {React.ReactElement} The billing encounter content component
 */
const BillingEncounterContent: FC = () => {
  const [billingEncounterList, setBillingEncounterList] = useState<BillingEncounterInterface[]>([]);
  const [billingFilter, setBillingFilter] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);

  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();

  const [appointmentList, setAppointmentList] = useState<AppointmentInterface[]>([]);
  const [patientList, setPatientList] = useState<PatientInterface[]>([]);
  const [patientModel, setPatientModel] = useState<Partial<PatientInterface>>();
  const [appointmentModel, setAppointmentModel] = useState<AppointmentInterface>();
  const [selectedAppointment, setSelectedAppointment] = useState<string>();
  const [patientIdFilter, setPatientIdFilter] = useState<string>();
  const [patientAutocompleteKey, setPatientAutocompleteKey] = useState<number>(0);
  const [appointmentAutocompleteKey, setAppointmentAutocompleteKey] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    return new Date(date.getFullYear(), 0, 1);
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  useEffect(() => {
    setPage(1);
  }, [billingFilter, patientIdFilter, selectedAppointment, startDate, endDate]);

  useEffect(() => {
    const nameParam = billingFilter ? `pricingName=${billingFilter}&` : "";
    const startDateParam = startDate ? `startDate=${formatDateForAPI(startDate, false)}&` : "";
    const endDateParam = endDate ? `endDate=${formatDateForAPI(endDate, true)}&` : "";
    const url = `${CoreAPI_URLs.BILLING_ENCOUNTER_GET_ALL}?${nameParam}${startDateParam}${endDateParam}page=${page}&pageSize=${pageSize}`;  
    const headers = [];
    if (selectedAppointment) {
      headers.push({
        header: HeaderEnum.APPOINTMENT_ID,
        value: selectedAppointment,
      });
    } else {
      headers.push({
        header: HeaderEnum.PATIENT_ID,
        value: patientIdFilter,
      });
    }
    sendRequest(url, onGetBillingEncounterHandler, "GET", undefined, headers.length ? headers : undefined);
  }, [billingFilter, patientIdFilter, page, pageSize, selectedAppointment, startDate, endDate]);

  const onGetBillingEncounterHandler = (response: QueryResultInterface<BillingEncounterInterface>) => {
    setBillingEncounterList(response.results);
    setRowsTotal(response.total);
    setPageTotal(Math.ceil(response.total / pageSize));
  };

  useEffect(() => {
    sendRequest(CoreAPI_URLs.PATIENTS_GET_ALL, onGetAllPatients, "GET");
  }, []);

  const onGetAllPatients = (data: QueryResultInterface<PatientInterface>) => {
    setPatientList(data.results);
  };

  const onGetPatientAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    setAppointmentList(data.results);
    const lastAppointment = data.results.length > 0 ? data.results[data.results.length - 1] : null;
    if (lastAppointment && !selectedAppointment) {
      setAppointmentModel(lastAppointment);
    }
  };

  useEffect(() => {
    if (!selectedAppointment) {
      setAppointmentAutocompleteKey(prev => prev + 1);
    }
    if (patientModel) {
      sendRequest(CoreAPI_URLs.APPOINTMENTS_GET_ALL, onGetPatientAppointments, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: patientModel.id,
        },
      ]);
    }
  }, [patientModel]);

  const patientOptions = useMemo<AutoCompleteOption[]>(() => {
    if (patientList.length === 0) {
      return [];
    } else {
      return patientList
        .filter((patient) => patient?.person?.name && patient?.person?.lastname)
        .map((a) => ({
          label: `${a.person.name} ${a.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [patientList]);

  const appointmentOptions = useMemo<AutoCompleteOption[]>(() => {
    if (appointmentList.length === 0) {
      return [];
    } else {
      return appointmentList
        .filter((appointment) => 
          appointment?.arrivalDateTime && 
          appointment?.provider?.person?.name && 
          appointment?.provider?.person?.lastname
        )
        .map((a) => {
          const dateStr = getFromISOYYYYMMDDDateFormat(new Date(a.arrivalDateTime));
          const providerName = `${a.provider.person.name} ${a.provider.person.lastname}`;
          return {
            label: `${dateStr} - ${providerName}`.toUpperCase(),
            id: a.id,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [appointmentList]);

  const onSelectPatientHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption | null
  ) => {
    if (newValue) {
      const selectedPatient = patientList.find((p) => p.id === newValue.id);
      setPatientModel(selectedPatient);
      setPatientIdFilter(newValue.id);
    } else {
      setPatientIdFilter("");
      setPatientModel(undefined);
    }
  };

  const onSelectAppointmentIdHandler = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption | null
  ) => {
    if (newValue) {
      const selectedAppointment = appointmentList.find((p) => p.id === newValue.id);
      if (selectedAppointment) {
        setSelectedAppointment(selectedAppointment.id);
        setAppointmentModel(selectedAppointment);
      }
    } else {
      setSelectedAppointment(undefined);
      setAppointmentModel(undefined);
    }
  };

  const handleClearFilters = () => {
    setPatientModel(undefined);
    setPatientIdFilter("");
    setSelectedAppointment(undefined);
    setAppointmentModel(undefined);
    setBillingFilter("");
    setPatientAutocompleteKey(prev => prev + 1);
    setAppointmentAutocompleteKey(prev => prev + 1);
    const today = new Date();
    setStartDate(new Date(today.getFullYear(), 0, 1));
    setEndDate(new Date());
    setPage(1);
  };

  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const newDate = new Date(e.target.value + 'T00:00:00');
      setStartDate(newDate);
    } else {
      setStartDate(null);
    }
  };

  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const newDate = new Date(e.target.value + 'T23:59:59');
      setEndDate(newDate);
    } else {
      setEndDate(null);
    }
  };

  const formatDateForInput = (date: Date): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5} lg={5}>
          <TextField
            label={t("billingEncounterDataContent.startDate") || "Start Date"}
            type="date"
            value={formatDateForInput(startDate)}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <TextField
            label={t("billingEncounterDataContent.endDate") || "End Date"}
            type="date"
            value={formatDateForInput(endDate)}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <Autocomplete
            key={patientAutocompleteKey}
            options={patientOptions}
            label={t("billingEncounterDataContent.selectPatient")}
            formikOutside={true}
            onChange={onSelectPatientHandler}
            noOptionsText={t("billingEncounterDataContent.noOptions")}
          />
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <Autocomplete
            key={appointmentAutocompleteKey}
            options={appointmentOptions}
            label={t("billingEncounterDataContent.selectAppointment")}
            formikOutside={true}
            onChange={onSelectAppointmentIdHandler}
            noOptionsText={t("billingEncounterDataContent.noOptions")}
          />
        </Grid>
        <Grid item xs={12} md={2} lg={2} sx={{ position: "sticky", top: 0 }}>
          <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW]}
            >
              <Button type="button" variant="contained" color="secondary" onClick={handleClearFilters}>
                {t("billingEncounterDataContent.clear")}
              </Button> 
            </PermissionWrapper>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <BillingEncounterTable
            modelList={billingEncounterList}
            setModelList={setBillingEncounterList}
            canSearchPlaceholder={t("billingEncounterDataContent.searchByPricingName")}
            setModelFilter={setBillingFilter}
            entriesPerPage={{ defaultValue: pageSize }}
            setPageState={setPage}
            setPageSizeState={setPageSize}
            apiPagination={true}
            apiPageTotal={pageTotal}
            apiRowsTotal={rowsTotal}
            apiPageCurrentIndex={page}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingEncounterContent;